import { Component, Input, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AppService } from "../../../../core/services/app.service";
import { HttpClient } from "@angular/common/http";
import { UploadEvent } from "ngx-file-drop";
import { FileSystemFileEntry } from "ngx-file-drop";
import { toUrlFriendlySlug } from "src/app/shared/utils/url-slug.util";
import { DigitalMemoryLogService } from "../../services/digital-memory-log.service";

@Component({
  selector: "app-mail-popup",
  templateUrl: "./mail-popup.component.html",
})
export class MailPopupComponent implements OnInit {
  @Input() model: any;
  @Input() digitalLodId: string = "";
  @Input() type: string = "";
  @Input() shipName: string = "";

  public static emailContactAPI = AppService.getApiUrl() + "/api/digitalMemoryLogs/sendEmail";
  public static multipleEmailAPI = AppService.getApiUrl() + "/api/digitalMemoryLogs/sendMultipleEmail";

  // CSV Upload properties
  csvFile: File | null = null;
  csvData: any[] = [];
  csvHeaders: string[] = []; // Store headers in original order
  isUploading = false;
  uploadProgress = 0;
  csvErrors: string[] = [];
  foundColumns: { [key: string]: string } = {}; // Store found column mappings
  showEmailTemplate = false;
  isSending = false; // Track single email sending state

  constructor(
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    protected http: HttpClient,
    private digitalMemoryLogService: DigitalMemoryLogService
  ) {}

  ngOnInit() {
    this.resolveShipName();
  }

  /**
   * Prefer API ship.name over URL slug from route
   */
  private resolveShipName(): void {
    const shipId =
      this.model && this.model.shipId != null
        ? this.model.shipId
        : null;
    if (!shipId) {
      return;
    }

    this.digitalMemoryLogService.getShipList().subscribe(
      (ships: any[]) => {
        const ship = (ships || []).find(s => String(s.id) === String(shipId));
        if (ship && ship.name) {
          this.shipName = ship.name;
        }
      },
      () => {
        // Keep existing shipName (slug/fallback)
      }
    );
  }

  // Manual close method
  closeModal() {
    try {
      this.activeModal.close();
    } catch (error) {
      this.activeModal.dismiss();
    }
  }

  // CSV Upload methods
  onCsvFileChange(event: any) {
    const file = event.target.files[0];
    // Check if file exists and has .csv extension
    if (file && (file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv"))) {
      this.csvFile = file;
      this.parseCsvFile(file);
    } else {
      this.toastr.error("Please select a valid CSV file", "Error");
    }
  }

  // Drag and drop methods
  public dropped(event: UploadEvent) {
    if (event.files) {
      const droppedFile = event.files[0];
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv")) {
            this.csvFile = file;
            this.parseCsvFile(file);
          } else {
            this.toastr.error("Please select a valid CSV file", "Error");
          }
        });
      } else {
        this.toastr.error("Only one file can be dropped", "Error");
      }
    }
  }

  parseCsvFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const csv = e.target.result;
      this.csvData = this.parseCsvData(csv);
      // Validate CSV format
      const validation = this.validateCsvFormat(this.csvData);
      this.csvErrors = validation.errors;

      if (validation.isValid) {
        this.toastr.success(`Parsed ${this.csvData.length} records from CSV`, "Success");
      } else {
        this.toastr.error(
          validation.errors[0] || "CSV file has validation errors. Required headers: email, firstName, lastName",
          "Error"
        );
      }
    };
    reader.readAsText(file);
  }

  parseCsvData(csvText: string): any[] {
    // Remove any BOM characters and normalize line endings
    const cleanText = csvText
      .replace(/^\uFEFF/, "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");
    const lines = cleanText.split("\n").filter(line => line.trim() !== "");
    if (lines.length < 1) {
      this.csvHeaders = [];
      return [];
    }

    // Detect delimiter (comma or semicolon)
    const firstLine = lines[0];
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const delimiter = semicolonCount > commaCount ? ";" : ",";
    // Parse headers exactly as provided (trim quotes/spaces only)
    const headers = lines[0]
      .split(delimiter)
      .map(header => header.trim().replace(/"/g, ""))
      .filter(header => header !== ""); // Filter out empty headers
    // Store headers in original order (excluding empty ones)
    this.csvHeaders = [...headers];

    if (lines.length < 2) {
      return [];
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(delimiter).map(value => value.trim().replace(/"/g, ""));
        const row: any = {};

        // Map values to their corresponding headers (only non-empty headers)
        headers.forEach((header, index) => {
          row[header] = values[index] || "";
        });

        data.push(row);
      }
    }
    return data;
  }

  validateCsvFormat(data: any[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const requiredHeaders = ["email", "firstName", "lastName"];
    this.foundColumns = {};

    const availableColumns =
      this.csvHeaders && this.csvHeaders.length > 0
        ? this.csvHeaders
        : data.length > 0
          ? Object.keys(data[0])
          : [];

    if (availableColumns.length === 0) {
      errors.push("CSV file is empty or missing headers. Required headers: email, firstName, lastName");
      return { isValid: false, errors };
    }

    // Headers must match exactly: email, firstName, lastName (case-sensitive)
    const missingHeaders = requiredHeaders.filter(header => availableColumns.indexOf(header) === -1);
    if (missingHeaders.length > 0) {
      errors.push(
        `Invalid CSV format. Required headers must be exactly: email, firstName, lastName. Missing: ${missingHeaders.join(
          ", "
        )}. Found: ${availableColumns.join(", ")}`
      );
      return { isValid: false, errors };
    }

    requiredHeaders.forEach(header => {
      this.foundColumns[header] = header;
    });

    if (data.length === 0) {
      errors.push("CSV file has no data rows");
      return { isValid: false, errors };
    }

    // Validate email format for all rows
    data.forEach((row, index) => {
      const emailValue = row["email"];
      if (!emailValue || !String(emailValue).trim()) {
        errors.push(`Missing email at row ${index + 1}`);
      } else if (!this.isValidEmail(String(emailValue).trim())) {
        errors.push(`Invalid email format at row ${index + 1}: ${emailValue}`);
      }
    });
    return { isValid: errors.length === 0, errors };
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  uploadCsvFile() {
    if (!this.csvFile) {
      this.toastr.error("Please select a CSV file first", "Error");
      return;
    }

    if (this.csvErrors.length > 0) {
      this.toastr.error("Please fix CSV validation errors before uploading", "Error");
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", this.csvFile);
    formData.append("digitalMemoryLogId", this.digitalLodId);

    this.http.post(MailPopupComponent.multipleEmailAPI, formData).subscribe(
      (response: any) => {
        this.isUploading = false;
        this.uploadProgress = 100;
        this.toastr.success("Multiple emails sent successfully!", "Success");
        this.csvFile = null;
        this.csvData = [];
        this.csvErrors = [];
        this.uploadProgress = 0;

        // Close modal with a small delay to ensure toast is shown
        setTimeout(() => {
          this.closeModal();
        }, 100);
      },
      error => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.toastr.error(error ? error.error.Message || "Failed to send multiple emails" : "", "Error");
      }
    );

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
      }
    }, 200);
  }

  removeCsvFile() {
    this.csvFile = null;
    this.csvData = [];
    this.csvHeaders = [];
    this.csvErrors = [];
    this.uploadProgress = 0;
    this.foundColumns = {}; // Reset column mappings
  }

  // Download CSV example
  downloadCsvExample() {
    const csvContent = this.generateCsvExample();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "email_contacts_example.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.toastr.success("CSV example downloaded successfully!", "Success");
    } else {
      this.toastr.error("Download not supported in this browser", "Error");
    }
  }

  private generateCsvExample(): string {
    // Create CSV content with headers and example data
    const headers = ["email", "firstName", "lastName"];
    const exampleData = [
      ["john.doe@example.com", "john", "doe"],
      ["jane.smith@example.com", "jane", "smith"],
      ["bob.wilson@example.com", "bob", "wilson"],
    ];

    // Add BOM for proper UTF-8 encoding
    const csvContent = "\uFEFF" + headers.join(",") + "\n" + exampleData.map(row => row.join(",")).join("\n");

    return csvContent;
  }

  // Helper methods for template
  getTotalColumns(): number {
    return this.csvData[0] ? Object.keys(this.csvData[0]).length : 0;
  }

  getRequiredColumnsFound(): number {
    return this.foundColumns ? Object.keys(this.foundColumns).length : 0;
  }

  getAdditionalColumnsCount(): number {
    if (!this.csvData[0] || !this.foundColumns) return 0;
    const allColumns = Object.keys(this.csvData[0]);
    const requiredColumns = Object.values(this.foundColumns);
    return allColumns.filter(col => !requiredColumns.includes(col)).length;
  }

  // Get CSV headers in original order
  getCsvHeaders(): string[] {
    return this.csvHeaders;
  }

  // Get CSV row values in original order
  getCsvRowValues(row: any): any[] {
    if (!row || !this.csvHeaders) return [];
    return this.csvHeaders.map(header => row[header] || "");
  }

  public sendInfo(data: any) {
    if (this.type === "multi") {
      // For multi-send, trigger CSV upload
      this.uploadCsvFile();
      return;
    }
    // For single send, send form data
    this.isSending = true;
    this.http
      .post(MailPopupComponent.emailContactAPI, {
        ...data,
        digitalMemoryLogId: this.digitalLodId,
      })
      .subscribe(
        (res: any) => {
          this.isSending = false;
          this.toastr.success("Email sent successfully!", "Success");
          // Safely update data properties if they exist in response
          if (res && res.Data) {
            if (res.Data.name) data.name = res.Data.name;
            if (res.Data.email) data.email = res.Data.email;
            if (res.Data.message) data.message = res.Data.message;
            if (res.Data.phone) data.phone = res.Data.phone;
          }

          // Close modal with a small delay to ensure toast is shown
          setTimeout(() => {
            this.closeModal();
          }, 100);
        },
        error => {
          this.isSending = false;
          this.toastr.error(error ? error.error.Message || "Send failed" : "", "Error");
        }
      );
  }
  openEmailTemplate() {
    this.showEmailTemplate = true;
  }
  onClosePreview() {
    this.showEmailTemplate = false;
  }

  getLogUrl(): string {
    const name = (this.model && this.model.name) || "";
    const id = this.digitalLodId || (this.model && this.model.id) || "";
    if (!id) {
      return "";
    }
    return `${window.location.origin}/preview-${toUrlFriendlySlug(name)}-${id}`;
  }
}
